import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'
import { useAuthStore } from '../store/auth'
import { useMyChurches } from '../hooks/useChurches'

/**
 * Wraps church admin routes (`/churches/:slug/...`).
 *
 * Requires authentication AND membership of the specific church in the URL —
 * having the global `church_admin` role for *some other* church is not enough.
 * Super admins may administer any church. Non-members are redirected to the
 * church's public profile. Per-church authorization is also enforced by the API
 * (each privileged endpoint returns 403 for non-members); this guard keeps the
 * admin UI from rendering for a church the user can't manage.
 */
export default function ChurchAdminRoute() {
  const { accessToken, user } = useAuthStore()
  const { slug } = useParams()
  const location = useLocation()
  const { data: myChurchesData, isLoading } = useMyChurches()

  if (!accessToken) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Super admins can administer any church.
  if (user?.role === 'super_admin') {
    return <Outlet />
  }

  // Must hold the church_admin role at all (a person account never qualifies).
  if (user && user.role !== 'church_admin') {
    return <Navigate to="/" replace />
  }

  // Wait for the membership list before deciding — avoids flashing a redirect.
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-[13px] text-muted">Loading…</div>
      </div>
    )
  }

  // Per-church check: the user must belong to THIS church.
  const isMember = Boolean(slug && myChurchesData?.data?.some((c) => c.slug === slug))
  if (!isMember) {
    return <Navigate to={slug ? `/churches/${slug}` : '/'} replace />
  }

  return <Outlet />
}
